import { call, put, select, takeLatest, all } from 'redux-saga/effects';
import { addToCartActions as actions } from '.';
import { shoppingCartActions } from '../../shoppingCart/slice';
import { notificationsActions } from '../../notifications/slice';
import {
  DiscAddToCartInput,
  DiscAddToCartPayload,
  FrameAddToCartInput,
  FrameAddToCartPayload,
  RingAddToCartInput,
  RingAddToCartPayload,
  StripAddToCartInput,
  StripAddToCartPayload,
} from './types';
import { PayloadAction } from '@reduxjs/toolkit';
import { Gasket } from '../../gaskets/slice/types';
import qs from 'qs';
import { Shape } from '../../shapeValidation/slice/types';
import {
  selectErpSystem,
  selectLanguage,
} from '../../erpSystem/slice/selectors';
import { selectNonStandardGasket } from '../../gaskets/slice/selectors';

export function* fetchAddToCart(
  shape: Shape,
  queryString: string,
  thumbnail: string,
) {
  const { addToCartFail, addToCartSuccess } = actions;

  try {
    const body = {
      thumbnail,
      action: `${shape}AddToCart`,
    };

    const bodyString = JSON.stringify(body);
    const addToCartResponse = yield fetch(
      `/gasket-configurator/api/configuredAddToCart?${queryString}`,
      {
        method: 'POST',
        body: bodyString,
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );
    if (addToCartResponse.status !== 200) {
      return yield put(addToCartFail('failed to fetch'));
    }

    const nonStandardGasket = yield select(selectNonStandardGasket);
    yield put(shoppingCartActions.getShoppingCart());
    yield put(
      notificationsActions.setNotification({
        customItemDescription: nonStandardGasket.customItemDescription,
        status: 'success',
        notificationKey: 'successAddToCart',
        isDismissed: false,
        timeoutId: new Date().getTime().toString(),
      }),
    );
    yield put(addToCartSuccess(true));
  } catch (e: any) {
    yield put(addToCartFail('failed to fetch'));
  }
}

export function* fetchRingAddToCart(action: PayloadAction<RingAddToCartInput>) {
  const {
    payload: {
      outerDiameter,
      innerDiameter,
      diameterOfBoltHoles,
      pitchCircle,
      numberOfBoltHoles,
      thumbnail,
    },
  } = action;

  const nonStandardGasket: Gasket = yield select(selectNonStandardGasket);
  if (!nonStandardGasket) {
    return yield put(
      actions.addToCartFail('There is no non standard gasket in the store'),
    );
  }
  const erpSystem = yield select(selectErpSystem);
  const language = yield select(selectLanguage);

  const addToCartPayload: RingAddToCartPayload = {
    erpPartNumber: nonStandardGasket.erpPartNumber,
    diameter: outerDiameter,
    internalDiameter: innerDiameter,
    ...(diameterOfBoltHoles !== '0'
      ? { diameterBoltholes: diameterOfBoltHoles }
      : {}),
    ...(pitchCircle !== '0' ? { pitchCircle: pitchCircle } : {}),
    ...(numberOfBoltHoles !== '0' ? { numberOfHoles: numberOfBoltHoles } : {}),
    erpSystem,
    lang: language,
    targetSystem: 'SEL_GAS',
    quantity: nonStandardGasket.price.itemQuantity.toString(),
    customItemDescription: nonStandardGasket.customItemDescription || '',
  };

  const querystring = qs.stringify(addToCartPayload);

  yield call(fetchAddToCart, 'ring', querystring, thumbnail);
}
export function* fetchDiscAddToCart(action: PayloadAction<DiscAddToCartInput>) {
  const {
    payload: {
      outerDiameter,
      numberOfBoltHoles,
      diameterOfBoltHoles,
      pitchCircle,
      thumbnail,
    },
  } = action;
  const nonStandardGasket: Gasket = yield select(selectNonStandardGasket);
  if (!nonStandardGasket) {
    return yield put(
      actions.addToCartFail('There is no non standard gasket in the store'),
    );
  }
  const erpSystem = yield select(selectErpSystem);
  const language = yield select(selectLanguage);
  const addToCartPayload: DiscAddToCartPayload = {
    erpPartNumber: nonStandardGasket.erpPartNumber,
    diameter: outerDiameter,
    ...(diameterOfBoltHoles !== '0'
      ? { diameterBoltholes: diameterOfBoltHoles }
      : {}),
    ...(numberOfBoltHoles !== '0' ? { numberOfHoles: numberOfBoltHoles } : {}),
    ...(pitchCircle !== '0' && typeof pitchCircle !== 'undefined'
      ? { pitchCircle }
      : {}),
    erpSystem,
    lang: language,
    targetSystem: 'SEL_GAS',
    quantity: nonStandardGasket.price.itemQuantity.toString(),
    customItemDescription: nonStandardGasket.customItemDescription || '',
  };
  const queryString = qs.stringify(addToCartPayload);
  yield call(fetchAddToCart, 'disc', queryString, thumbnail);
}
export function* fetchFrameAddToCart(
  action: PayloadAction<FrameAddToCartInput>,
) {
  const {
    payload: {
      outsideLength,
      outsideWidth,
      insideLength,
      insideWidth,
      outsideRadius,
      insideRadius,
      thumbnail,
    },
  } = action;
  const nonStandardGasket: Gasket = yield select(selectNonStandardGasket);
  if (!nonStandardGasket) {
    return yield put(
      actions.addToCartFail('There is no non standard gasket in the store'),
    );
  }

  const erpSystem = yield select(selectErpSystem);
  const language = yield select(selectLanguage);
  const addToCartPayload: FrameAddToCartPayload = {
    outsideWidth,
    outsideLength,
    insideLength,
    insideWidth,
    outsideRadius,
    insideRadius,
    erpPartNumber: nonStandardGasket.erpPartNumber,
    erpSystem,
    lang: language,
    targetSystem: 'SEL_GAS',
    quantity: nonStandardGasket.price.itemQuantity.toString(),
    customItemDescription: nonStandardGasket.customItemDescription || '',
  };

  const queryString = qs.stringify(addToCartPayload);
  yield call(fetchAddToCart, 'frame', queryString, thumbnail);
}
export function* fetchStripAddToCart(
  action: PayloadAction<StripAddToCartInput>,
) {
  const {
    payload: { length, width, thumbnail },
  } = action;
  const erpSystem = yield select(selectErpSystem);
  const language = yield select(selectLanguage);
  const nonStandardGasket: Gasket = yield select(selectNonStandardGasket);
  if (!nonStandardGasket) {
    return yield put(
      actions.addToCartFail('There is no singleItem in the store'),
    );
  }

  const addToCartPayload: StripAddToCartPayload = {
    length,
    width,
    erpPartNumber: nonStandardGasket.erpPartNumber,
    erpSystem,
    lang: language,
    targetSystem: 'SEL_GAS',
    quantity: nonStandardGasket.price.itemQuantity.toString(),
    customItemDescription: nonStandardGasket.customItemDescription || '',
  };

  const queryString = qs.stringify(addToCartPayload);
  yield call(fetchAddToCart, 'strip', queryString, thumbnail);
}

export function* ringAddToCartSaga() {
  yield takeLatest(actions.addRingToCart.type, fetchRingAddToCart);
}

export function* stripAddToCartSaga() {
  yield takeLatest(actions.addStripToCart.type, fetchStripAddToCart);
}

export function* frameAddToCartSaga() {
  yield takeLatest(actions.addFrameToCart.type, fetchFrameAddToCart);
}

export function* discAddToCartSaga() {
  yield takeLatest(actions.addDiscToCart.type, fetchDiscAddToCart);
}

export function* addToCartSaga() {
  yield all([
    ringAddToCartSaga(),
    stripAddToCartSaga(),
    frameAddToCartSaga(),
    discAddToCartSaga(),
  ]);
}
